import { object, string } from "yup";

/**
 * Slugify a display name into a taxonomy key: lowercase, å/ä → a, ö → o,
 * other diacritics stripped, everything non-alphanumeric collapsed to "-".
 * Used as a live preview of the key the server generates for tag types, and
 * to generate keys client-side for org fields saved via whole-array replace
 * (e.g. certification types).
 */
export const slugifyKey = (name: string) =>
	name
		.trim()
		.toLowerCase()
		.replace(/[åä]/g, "a")
		.replace(/ö/g, "o")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

export const convertToSentenceCase = (text?: string) => {
	const firstChar: string = text?.charAt(0) || "";
	const lowercase: string = text?.replace(/_/g, " ").toLowerCase() || "";
	return firstChar.toUpperCase() + lowercase.substring(1, lowercase.length);
};

export const generateAvatarString = (str?: string, maxLength?: number) => {
	if (!str) return "?";

	let output = "",
		i = 0;
	const newStr = str.split(" ");
	const len = newStr.length > 5 ? 5 : newStr.length;

	for (i; i < len; i++) if (newStr[i] !== "") output += newStr[i][0];
	return (maxLength ? output.substring(0, maxLength) : output).toUpperCase();
};

interface CurrencyOptions {
	currency?: string;
	maximumFractionDigits?: number;
	hideCurrency?: boolean;
}

export const formatCurrencyString = (amount: string | number, options: CurrencyOptions = {}) => {
	const currency = options.currency || "SEK";
	const formatter = new Intl.NumberFormat("sv-SE", {
		style: "currency",
		currency,
		maximumFractionDigits: options.maximumFractionDigits || 0,

		// These options are needed to round to whole numbers if that's what you want.
		//minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
		//maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
	});

	let formattedNumber = formatter.format(Number(amount));

	// Hack to replace "kr" with "SEK", there might be a better way...
	formattedNumber = formattedNumber.replace("kr", "SEK");

	formattedNumber = options.hideCurrency ? formattedNumber.replace(currency, "") : formattedNumber;

	return formattedNumber;
};

export const formatCurrencyStringForKPI = (
	amount: string | number,
	options: CurrencyOptions = {}
) => {
	const currency = options.currency || "SEK";
	const formatter = new Intl.NumberFormat("sv-SE", {
		style: "decimal",
		currency,
		maximumFractionDigits: options.maximumFractionDigits || 0,

		// These options are needed to round to whole numbers if that's what you want.
		//minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
		//maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
	});

	let formattedNumber = formatter.format(Number(amount));

	// Hack to replace "kr" with "SEK", there might be a better way...
	formattedNumber = formattedNumber.replace("kr", "SEK");

	formattedNumber = options.hideCurrency ? formattedNumber.replace(currency, "") : formattedNumber;

	return formattedNumber;
};

export function decimalToPercentage(value: string | number) {
	const num = Number(value);
	return Math.round(num * 100) + "%";
}

const emailSchema = object({
	email: string().email(),
});

export function isValidEmail(input: string) {
	return emailSchema.isValidSync({ email: input });
}

export function toHoursAndMinutes(totalMinutes: number) {
	const minutes = totalMinutes % 60;
	const hours = Math.floor(totalMinutes / 60);

	const hoursStr = hours ? `${hours} h` : "";
	const minutesStr = minutes ? `${minutes} min` : "";

	return `${hoursStr} ${minutesStr}`.trim();
}

export function randomUuid() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		var r = (Math.random() * 16) | 0,
			v = c == "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export function addDotsIfLong(stringValue: string, maxLength: number) {
	if (stringValue.length > maxLength) {
		return stringValue.slice(0, maxLength) + "...";
	} else {
		return stringValue;
	}
}

/**
 * Checks if the given scope pattern matches any of the given scopes.
 * Accepts wildcard in pattern, for example `report.*` will match `report.read` and `report.write`.
 * @param scopePattern
 * @param scopes
 */
export function matchScope(scopePattern: string, scopes: string[]) {
	const scopePatternParts = scopePattern.split(".");
	const scopesMatched = scopes.filter((scope) => {
		const scopeParts = scope.split(".");
		if (scopeParts.length !== scopePatternParts.length) return false;

		for (let i = 0; i < scopeParts.length; i++) {
			if (scopePatternParts[i] !== "*" && scopePatternParts[i] !== scopeParts[i]) return false;
		}

		return true;
	});

	return scopesMatched.length > 0;
}

export function validateAndFormatURL(url: string) {
	if (!url.startsWith("http://") && !url.startsWith("https://")) {
		return "https://" + url;
	}
	return url;
}
